export const MAX_COPAY_DEDUCTIBLE_TO_DISPLAY_AMOUNT = 40;

export enum PatientIntakeQuestionType {
  a1c = "a1c",
  address = "address",
  alcoholFrequency = "alcoholFrequency",
  altoPharmacy = "altoPharmacy",
  bariatricSurgery = "bariatricSurgery",
  historyBariatricSurgery = "historyBariatricSurgery",
  bloodPressure = "bloodPressure",
  choosePharmacy = "choosePharmacy",
  conditions = "conditions",
  confirmUser = "confirmUser",
  eatingDisorder = "eatingDisorder",
  familyHistory = "familyHistory",
  heartRate = "heartRate",
  heightWeight = "heightWeight",
  labs = "labs",
  labUploader = "labUploader",
  medication = "medication",
  useMedications = "useMedications",
  medicationAllergies = "medicationAllergies",
  hasMedicationAllergies = "hasMedicationAllergies",
  medicationAllergiesList = "medicationAllergiesList",
  medicationList = "medicationList",
  medications = "medications",
  notAvailable = "notAvailable",
  originalConditions = "originalConditions",
  otherSurgeries = "otherSurgeries",
  hasOtherSurgeries = "hasOtherSurgeries",
  otherSurgeriesList = "otherSurgeriesList",
  patientAcknowledgement = "patientAcknowledgement",
  patientAttestation = "patientAttestation",
  phoneNumber = "phoneNumber",
  pregnantOrBreastfeeding = "pregnantOrBreastfeeding",
  searchPharmacy = "searchPharmacy",
  selfAssessedHealth = "selfAssessedHealth",
  suicideOrSelfHarm = "suicideOrSelfHarm",
  useDrugs = "useDrugs",
  useTobacco = "useTobacco",
  // Below are SteadyMD Intake
  familyHistoryThyroidCancer = "familyHistoryThyroidCancer",
  conditionsExisting = "conditionsExisting",
  bloodPressureSimple = "bloodPressureSimple",
  heartRateSimple = "heartRateSimple",
  lifestyleImpacts = "lifestyleImpacts",
  suicideOrSelfHarm1 = "suicideOrSelfHarm1",
  suicideOrSelfHarm2 = "suicideOrSelfHarm2",
  // SteadyMD Insurance Intake
  insuranceCoverage = "insuranceCoverage",
  insurancePayer = "insurancePayer",
  insuranceIsPrimarySubscriber = "insuranceIsPrimarySubscriber",
  insurancePrimarySubscriberName = "insurancePrimarySubscriberName",
  insurancePrimarySubscriberDob = "insurancePrimarySubscriberDob",
  insuranceMemberId = "insuranceMemberId",
  insuranceGroupId = "insuranceGroupId",
  insuranceRxBin = "insuranceRxBin",
  insuranceRxPcn = "insuranceRxPcn",
  insuranceRxGroup = "insuranceRxGroup",
  // Below are keys that are returned from requiredPatientIntake
  // The discrepancy above is that we may get back keys that dont send
  // i.e. "bmi" but still want to handle as PatientIntakeQuestion types
  bmi = "bmi",
  height = "height",
}

export const enum SchedulerFlow {
  INITIAL = "initial",
  FOLLOWUP = "followup",
  INITIAL_VIDEO_VISIT = "initial_video_visit",
}

export const enum ConsultType {
  EM = "EM",
  RPM = "RPM",
  VIDEO = "VIDEO",
}

export function getConsultTypeForSchedulerFlow(
  flow: SchedulerFlow
): ConsultType {
  switch (flow) {
    case SchedulerFlow.FOLLOWUP:
    case SchedulerFlow.INITIAL:
    default:
      return null;
  }
}

export function getSchedulerFlowForConsultType(
  consultType: ConsultType
): SchedulerFlow {
  switch (consultType) {
    case ConsultType.EM:
      return SchedulerFlow.INITIAL;
    case ConsultType.RPM:
      return SchedulerFlow.FOLLOWUP;
    default:
      return null;
  }
}

export enum EligibleStateCode {
  AZ = "AZ",
  CA = "CA",
  CT = "CT",
  FL = "FL",
  GA = "GA",
  IA = "IA",
  MT = "MT",
  NH = "NH",
  PA = "PA",
  TX = "TX",
  VA = "VA",
}

export enum IneligibleStateCodeForNewPatients {
  AZ = "AZ",
  CA = "CA",
  CT = "CT",
  FL = "FL",
  GA = "GA",
  IA = "IA",
  MT = "MT",
  NH = "NH",
  PA = "PA",
  TX = "TX",
  VA = "VA",
}

export enum EligibleStateCodeNoomClinical {
  AR = "AR",
  AZ = "AZ",
  CA = "CA",
  CO = "CO",
  CT = "CT",
  FL = "FL",
  GA = "GA",
  IA = "IA",
  ID = "ID",
  IL = "IL",
  IN = "IN",
  KS = "KS",
  KY = "KY",
  MA = "MA",
  MD = "MD",
  ME = "ME",
  MI = "MI",
  MN = "MN",
  MO = "MO",
  MS = "MS",
  MT = "MT",
  NC = "NC",
  NE = "NE",
  NH = "NH",
  NY = "NY",
  OH = "OH",
  OK = "OK",
  PA = "PA",
  RI = "RI",
  SC = "SC",
  TN = "TN",
  TX = "TX",
  UT = "UT",
  VT = "VT",
  WA = "WA",
  WI = "WI",
}

// This was being used UserSegment/features.ts in combination with a BMI of >= 25 to determine eligibility for telehealth.
// That has been stopped for now, but we're keeping this here in case we need it again in the future.
export const ELIGIBLE_HEALTH_RISKS = [
  "heartDiseaseOrStroke",
  "highBloodPressure",
  "diabetes",
  "nash",
  "kidneyDisease",
  "highCholesterol",
  "asthma",
  "insomnia",
];

export const INELIGIBLE_HEALTH_RISKS = [
  "type1Diabetes",
  "activeHeartDiseaseOrFailure",
  "liverFailure",
  "cancerUnderTreatment",
];

export type PayerInfo = {
  payerId: string;
  workflowId: string;
};

export enum Payer {
  aetna = "aetna",
  anthem = "anthem",
  bsca = "bsca",
  bcbs = "bcbs",
  cigna = "cigna",
  humana = "humana",
  kaiserPermanente = "kaiserPermanente",
  medicare = "medicare",
  tricare = "tricare",
  uhc = "uhc",
}

export enum SurveyType {
  // create a new consult
  Schedule = "Schedule",
  // move an existing consult to a new time
  Reschedule = "Reschedule",
}

export const EligiblePayerMappings: {
  [stateCode in EligibleStateCode]: {
    [payer in Payer]?: PayerInfo;
  };
} = {
  CA: {
    anthem: { payerId: "47198", workflowId: "weight-management-anthem" },
    bsca: { payerId: "BS001", workflowId: "weight-management-bsca" },
    uhc: { payerId: "87726", workflowId: "weight-management-uhc" },
    aetna: { payerId: "60054", workflowId: "weight-management-aetna" },
  },
  TX: {
    uhc: { payerId: "87726", workflowId: "weight-management-uhc" },
    bcbs: {
      payerId: "84980",
      workflowId: "weight-management-bcbs-tx",
    },
  },
  FL: {
    bcbs: { payerId: "00590", workflowId: "weight-management-bcbs-fl" },
    aetna: { payerId: "60054", workflowId: "weight-management-aetna-fl" },
  },
  GA: {
    aetna: { payerId: "60054", workflowId: "new-patient-aetna" },
    // Payer id for anthem for this state is different
    anthem: { payerId: "00601", workflowId: "new-patient-anthem" },
  },
  IA: {
    bcbs: { payerId: "88848", workflowId: "new-patient-bcbs-ia" },
  },
  CT: {
    anthem: { payerId: "00060", workflowId: "weight-management-anthem" },
    aetna: { payerId: "60054", workflowId: "weight-management-aetna" },
  },
  AZ: {
    aetna: { payerId: "60054", workflowId: "weight-management-aetna" },
    uhc: { payerId: "87726", workflowId: "weight-management-uhc" },
  },
  VA: {
    aetna: { payerId: "60054", workflowId: "weight-management-aetna" },
    uhc: { payerId: "87726", workflowId: "new-patient-uhc" },
  },
  MT: {
    bcbs: { payerId: "BCBS", workflowId: "new-patient-bcbs-mt" },
  },
  NH: {
    anthem: { payerId: "00770", workflowId: "new-patient-anthem" },
  },
  PA: {
    bcbs: { payerId: "54771", workflowId: "new-patient-bcbs-pa" },
  },
};

export const CONSENT_CHECKBOX_ID = "consents";

export const TELEHEALTH_ADDON_PREFIX =
  "telehealth-access-self-pay-subscription-";

// When existing users sign up for Noom Med, we want to offer them the 1 month
// 0 day trial plan.
export const NOOM_MED_OFFER_PLAN_ID = `${TELEHEALTH_ADDON_PREFIX}1m-0t`;
